import { c as _c } from "react/compiler-runtime";
import { ProductTypeEnum, StoreImage, StoreItemEnum, ThumbnailBaseCommonType } from '@systemeio/digital-store-shared';
import { ActionMenu, ActionMenuItem, toast } from '@systemeio/ui';
import { ItemDeleteModal } from 'modules/stores/store/components/item-delete-modal';
import { STORE_ITEM_CLASS_NAME } from 'modules/stores/store/constants/store-item-class-name';
import { storeItemProductTitleLocoKeys, storeItemTitleLocoKeys } from 'modules/stores/store/constants/store-item-constants';
import { useStoreQuery } from 'modules/stores/store/hooks/use-store-query';
import { StoreItemWithTabType } from 'modules/stores/store/types/store-item-interface';
import { getImageTypeFromItem } from 'modules/stores/store/utils/get-image-type-from-item';
import { getTabFromItem } from 'modules/stores/store/utils/get-tab-from-item';
import React, { FC } from 'react';
import Confirmation from 'shared/components/confirmation-modal';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useModal } from 'shared/hooks/use-modal';
import { handle422Error } from 'shared/utils/parse-422-errors';
import { twJoin } from 'tailwind-merge';
import { StoreInterface } from '../../types/stores-interface';
import { useStoreEditItemContext } from '../context/store-item-edit-context';
import { useStore } from '../hooks/use-store';
type Props = {
  itemType: Exclude<StoreItemWithTabType, StoreItemEnum.product>;
  thumbnail: ThumbnailBaseCommonType['thumbnail'];
  productType?: never;
  title: string;
  itemId: number;
  onDelete: () => Promise<void>;
  onSaveDraft: () => Promise<void>;
  onPublish: () => Promise<void>;
  draft: boolean;
} | {
  itemType: Extract<StoreItemWithTabType, StoreItemEnum.product>;
  productType: ProductTypeEnum;
  thumbnail: ThumbnailBaseCommonType['thumbnail'];
  title: string;
  itemId: number;
  onDelete: () => Promise<void>;
  onSaveDraft: () => Promise<void>;
  onPublish: () => Promise<void>;
  draft: boolean;
};
function isProductItem(props: Props): props is Extract<Props, {
  itemType: StoreItemEnum.product;
}> {
  return props.itemType === StoreItemEnum.product;
}
const ItemContent: FC<Props> = props => {
  const $ = _c(99);
  const {
    thumbnail,
    draft,
    title,
    itemId,
    onDelete,
    onSaveDraft,
    onPublish
  } = props;
  const {
    setErrors
  } = useStoreEditItemContext();
  const {
    t
  } = useLocoTranslation();
  const {
    onEditItem,
    storeId
  } = useStoreQuery();
  const {
    mutate
  } = useStore(storeId);
  const saveDraftModal = useModal();
  const publishModal = useModal();
  const deleteModal = useModal();
  let t0;
  if ($[0] !== itemId || $[1] !== onEditItem || $[2] !== props.itemType || $[3] !== props.productType) {
    t0 = async () => {
      await onEditItem(itemId, getTabFromItem(props.itemType), props.productType);
    };
    $[0] = itemId;
    $[1] = onEditItem;
    $[2] = props.itemType;
    $[3] = props.productType;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const openEditItem = t0;
  let t1;
  if ($[5] !== openEditItem) {
    t1 = async e => {
      const target = e.target as HTMLDivElement;
      const button = target.closest("button");
      const link = target.closest("a");
      const modalBackground = target.closest("#modal-background");
      if (button || link || modalBackground) {
        return;
      }
      await openEditItem();
    };
    $[5] = openEditItem;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const onItemClick = t1;
  let t2;
  if ($[7] !== props) {
    t2 = isProductItem(props) ? getImageTypeFromItem({
      itemType: props.itemType,
      productType: props.productType
    }) : getImageTypeFromItem({
      itemType: props.itemType
    });
    $[7] = props;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const imageType = t2;
  const itemTitle = isProductItem(props) ? storeItemProductTitleLocoKeys[props.productType] : storeItemTitleLocoKeys[props.itemType];
  let t3;
  if ($[9] !== itemId || $[10] !== mutate) {
    t3 = async draft_0 => {
      await mutate(data => {
        if (!data) {
          return;
        }
        const newData = {
          ...data
        };
        newData.items = data.items.map(item => {
          if (item.itemId === itemId) {
            return {
              ...item,
              draft: draft_0
            };
          }
          return item;
        });
        return newData;
      });
    };
    $[9] = itemId;
    $[10] = mutate;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const onMutateDraftStatus = t3;
  let t4;
  if ($[12] !== onMutateDraftStatus || $[13] !== onSaveDraft || $[14] !== openEditItem || $[15] !== setErrors || $[16] !== t) {
    t4 = async () => {
      ;
      try {
        setErrors({});
        await onSaveDraft();
        await onMutateDraftStatus(true);
      } catch (t5) {
        const e_0 = t5;
        handle422Error(e_0, setErrors);
        toast.error(t("global.error"));
        await openEditItem();
        throw e_0;
      }
    };
    $[12] = onMutateDraftStatus;
    $[13] = onSaveDraft;
    $[14] = openEditItem;
    $[15] = setErrors;
    $[16] = t;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const onSaveDraftConfirm = t4;
  let t5;
  if ($[18] !== onMutateDraftStatus || $[19] !== onPublish || $[20] !== openEditItem || $[21] !== setErrors || $[22] !== t) {
    t5 = async () => {
      ;
      try {
        setErrors({});
        await onPublish();
        await onMutateDraftStatus(false);
      } catch (t6) {
        const e_1 = t6;
        handle422Error(e_1, setErrors);
        toast.error(t("global.error"));
        await openEditItem();
        throw e_1;
      }
    };
    $[18] = onMutateDraftStatus;
    $[19] = onPublish;
    $[20] = openEditItem;
    $[21] = setErrors;
    $[22] = t;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const onPublishConfirm = t5;
  const t6 = draft ? "border-l-brand-primary" : "border-l-text-primary";
  let t7;
  if ($[24] !== t6) {
    t7 = twJoin(STORE_ITEM_CLASS_NAME, "flex w-full cursor-pointer items-center gap-3 border border-l-4 border-transparent sm:gap-5", t6);
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  const t8 = thumbnail?.thumbnail ?? thumbnail?.path;
  let t9;
  if ($[26] !== imageType || $[27] !== t8 || $[28] !== title) {
    t9 = <StoreImage imageType={imageType} src={t8} skipTheme={true} alt={title} className="min-w-12 sm:min-w-16 w-12 rounded-xl sm:w-16" />;
    $[26] = imageType;
    $[27] = t8;
    $[28] = title;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== itemTitle || $[31] !== t) {
    t10 = t(itemTitle);
    $[30] = itemTitle;
    $[31] = t;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10) {
    t11 = <span className="text-brand-primary">{t10}</span>;
    $[33] = t10;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      wordBreak: "break-word"
    };
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== title) {
    t13 = <b className="text-text-primary text-[16px] sm:text-[18px]" style={t12}>{title}</b>;
    $[36] = title;
    $[37] = t13;
  } else {
    t13 = $[37];
  }
  let t14;
  if ($[38] !== t11 || $[39] !== t13) {
    t14 = <div className="flex flex-col items-start gap-1">{t11}{t13}</div>;
    $[38] = t11;
    $[39] = t13;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== draft || $[42] !== t) {
    t15 = draft && <span className="bg-background-primary text-brand-primary rounded-lg p-2">{t("dashboard.store.item.draft")}</span>;
    $[41] = draft;
    $[42] = t;
    $[43] = t15;
  } else {
    t15 = $[43];
  }
  let t16;
  if ($[44] !== draft || $[45] !== saveDraftModal || $[46] !== t) {
    t16 = !draft ? <ActionMenuItem key="action-save-as-draft" onClick={() => {
      saveDraftModal.open();
    }} label={t("dashboard.store.item.save_draft")} /> : undefined;
    $[44] = draft;
    $[45] = saveDraftModal;
    $[46] = t;
    $[47] = t16;
  } else {
    t16 = $[47];
  }
  let t17;
  if ($[48] !== draft || $[49] !== publishModal || $[50] !== t) {
    t17 = draft ? <ActionMenuItem key="action-publish" onClick={() => {
      publishModal.open();
    }} label={t("dashboard.store.item.publish")} /> : undefined;
    $[48] = draft;
    $[49] = publishModal;
    $[50] = t;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== deleteModal) {
    t18 = () => {
      deleteModal.open();
    };
    $[52] = deleteModal;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== t) {
    t19 = t("global.delete");
    $[54] = t;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  let t20;
  if ($[56] !== t18 || $[57] !== t19) {
    t20 = <ActionMenuItem key="action-delete" onClick={t18} label={t19} />;
    $[56] = t18;
    $[57] = t19;
    $[58] = t20;
  } else {
    t20 = $[58];
  }
  let t21;
  if ($[59] !== t16 || $[60] !== t17 || $[61] !== t20) {
    t21 = <ActionMenu menuItems={[t16, t17, t20].filter(_temp)} />;
    $[59] = t16;
    $[60] = t17;
    $[61] = t20;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  const t22 = saveDraftModal.opened;
  const t23 = saveDraftModal.close;
  let t24;
  if ($[63] !== t) {
    t24 = t("dashboard.store.item.save_draft_confirmation");
    $[63] = t;
    $[64] = t24;
  } else {
    t24 = $[64];
  }
  let t25;
  if ($[65] !== t) {
    t25 = t("dashboard.store.item.save_draft_success");
    $[65] = t;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== onSaveDraftConfirm || $[68] !== saveDraftModal.close || $[69] !== saveDraftModal.opened || $[70] !== t24 || $[71] !== t25) {
    t26 = <Confirmation opened={t22} onClose={t23} onConfirm={onSaveDraftConfirm} confirmationText={t24} toastCaption={t25} />;
    $[67] = onSaveDraftConfirm;
    $[68] = saveDraftModal.close;
    $[69] = saveDraftModal.opened;
    $[70] = t24;
    $[71] = t25;
    $[72] = t26;
  } else {
    t26 = $[72];
  }
  const t27 = publishModal.opened;
  const t28 = publishModal.close;
  let t29;
  if ($[73] !== t) {
    t29 = t("dashboard.store.item.publish_confirmation");
    $[73] = t;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  let t30;
  if ($[75] !== t) {
    t30 = t("dashboard.store.item.publish_success");
    $[75] = t;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== onPublishConfirm || $[78] !== publishModal.close || $[79] !== publishModal.opened || $[80] !== t29 || $[81] !== t30) {
    t31 = <Confirmation opened={t27} onClose={t28} onConfirm={onPublishConfirm} confirmationText={t29} toastCaption={t30} />;
    $[77] = onPublishConfirm;
    $[78] = publishModal.close;
    $[79] = publishModal.opened;
    $[80] = t29;
    $[81] = t30;
    $[82] = t31;
  } else {
    t31 = $[82];
  }
  let t32;
  if ($[83] !== deleteModal.close || $[84] !== deleteModal.opened || $[85] !== onDelete) {
    t32 = <ItemDeleteModal onDelete={onDelete} opened={deleteModal.opened} onClose={deleteModal.close} />;
    $[83] = deleteModal.close;
    $[84] = deleteModal.opened;
    $[85] = onDelete;
    $[86] = t32;
  } else {
    t32 = $[86];
  }
  let t33;
  if ($[87] !== t15 || $[88] !== t21 || $[89] !== t26 || $[90] !== t31 || $[91] !== t32) {
    t33 = <div className="ml-auto flex items-center gap-3">{t15}{t21}{t26}{t31}{t32}</div>;
    $[87] = t15;
    $[88] = t21;
    $[89] = t26;
    $[90] = t31;
    $[91] = t32;
    $[92] = t33;
  } else {
    t33 = $[92];
  }
  let t34;
  if ($[93] !== onItemClick || $[94] !== t14 || $[95] !== t33 || $[96] !== t7 || $[97] !== t9) {
    t34 = <div className={t7} onClick={onItemClick}>{t9}{t14}{t33}</div>;
    $[93] = onItemClick;
    $[94] = t14;
    $[95] = t33;
    $[96] = t7;
    $[97] = t9;
    $[98] = t34;
  } else {
    t34 = $[98];
  }
  return t34;
};
export default ItemContent;
function _temp(e_2) {
  return !!e_2;
}